import React, { useMemo } from 'react';

import AddIcon from '@mui/icons-material/Add';
import AutoFixNormalIcon from '@mui/icons-material/AutoFixNormal';
import DeleteIcon from '@mui/icons-material/Delete';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useParams } from 'react-router-dom';

import { useCompiledStepsContext } from 'client/app/apps/disco-prototype/CompiledStepsProvider';
import ChunkyButton from 'client/app/apps/disco-prototype/components/ChunkyButton';
import FlexContainer from 'client/app/apps/disco-prototype/components/FlexContainer';
import PageNavigation, {
  PageSection,
} from 'client/app/apps/disco-prototype/components/PageNavigation';
import SimpleTable from 'client/app/apps/disco-prototype/components/SimpleTable';
import { StepSelect, ValueInput } from 'client/app/apps/disco-prototype/components/Steps';
import StepCheckbox from 'client/app/apps/disco-prototype/components/Steps/StepSelector/StepCheckbox';
import { discoPrototypeRoutes } from 'client/app/apps/disco-prototype/DiscoPrototypeRouter';
import { ErrorsAlert } from 'client/app/apps/disco-prototype/MakeMixturePage/ErrorsAlert';
import { MakeMixtureAction } from 'client/app/apps/disco-prototype/state/actions/makeMixtureAction';
import { useStateContext } from 'client/app/apps/disco-prototype/StateProvider';
import { queryTable } from 'common/types/disco/table';

const modeOptions = [
  { label: 'volume', value: 'volume' },
  { label: 'concentration', value: 'concentration' },
  { label: 'diluent', value: 'diluent' },
];

const Page = () => {
  const { stepId, actionId } = useParams<{ stepId: string; actionId: string }>();

  const { handleCalculateVolumes } = useCompiledStepsContext();
  const { steps, handleUpdateStep } = useStateContext();

  const step = steps?.find(step => step.id === stepId)!;
  const actions = step.actions || [];
  const action = actions.find(action => action.id === actionId)! as MakeMixtureAction;
  const errors = [
    ...(action.compiled?.errors ?? []),
    ...action.additions.flatMap(v => v.compiled?.errors ?? []),
  ];

  const nameColumn = action.source?.liquidProps.name.selectedColumnName!;
  const uniqueColumnNames = [
    ...new Set(queryTable(action.source?.table).findColumn(nameColumn) ?? []),
  ];
  const liquidOptions = uniqueColumnNames.map(ucn => ({ value: ucn, label: ucn }));
  const columnOptions =
    step.target?.table.columnNames.map(name => ({ value: name, label: name })) || [];

  const additions = action.additions;
  const finalComposition = action.finalComposition;

  const componentsOptions =
    action.source?.liquidProps.composition.map(c => ({
      label: c.name.value!,
      value: c.name.value!,
    })) || [];

  const { tableHeaders, tableColumns } = useMemo(() => {
    const baseHeaders = ['Liquid name', 'Wells'];
    const additionsHeaders = additions.map(addition => addition.sourceName);
    const baseColumns = [
      step.target?.liquidProps.name.selectedColumnName!,
      step.target?.liquidProps.well.selectedColumnName!,
    ];
    const additionsColumns = additions.reduce<string[]>(
      (acc, value) => [...acc, value.compiled?.volume.name!],
      [],
    );

    return {
      tableHeaders: [...baseHeaders, ...additionsHeaders],
      tableColumns: [...baseColumns, ...additionsColumns].filter(column => column),
    };
  }, [
    additions,
    step.target?.liquidProps.name.selectedColumnName,
    step.target?.liquidProps.well.selectedColumnName,
  ]);

  const handleAddLiquid = () => {
    handleUpdateStep(stepId, {
      type: 'updateAction',
      toUpdate: actionId,
      update: {
        type: 'makeMixture',
        additions: { type: 'create', name: liquidOptions[0].value },
      },
    });
  };

  const handleIsConstrained = (isConstrained: boolean) => {
    handleUpdateStep(stepId, {
      type: 'updateAction',
      toUpdate: actionId,
      update: {
        type: 'makeMixture',
        isConstrained,
      },
    });
  };

  const handleDeleteLiquid = (id: string) => {
    handleUpdateStep(stepId, {
      type: 'updateAction',
      toUpdate: actionId,
      update: { type: 'makeMixture', additions: { type: 'delete', toDelete: id } },
    });
  };

  const handleUpdateLiquid = (id: string, sourceName: string) => {
    handleUpdateStep(stepId, {
      type: 'updateAction',
      toUpdate: actionId,
      update: {
        type: 'makeMixture',
        additions: { type: 'update', toUpdate: id, sourceName },
      },
    });
  };

  const handleAddFinalComposition = () => {
    handleUpdateStep(stepId, {
      type: 'updateAction',
      toUpdate: actionId,
      update: {
        type: 'makeMixture',
        finalComposition: { type: 'create', name: componentsOptions[0].value },
      },
    });
  };

  const handleAutoFinalComposition = () => {
    handleUpdateStep(stepId, {
      type: 'updateAction',
      toUpdate: actionId,
      update: {
        type: 'makeMixture',
        finalComposition: { type: 'auto' },
      },
    });
  };

  const handleDeleteFinalComposition = (id: string) => {
    handleUpdateStep(stepId, {
      type: 'updateAction',
      toUpdate: actionId,
      update: { type: 'makeMixture', finalComposition: { type: 'delete', toDelete: id } },
    });
  };

  const handleUpdateFinalComposition =
    (id: string, field: string) => (key: string, value: string) => {
      handleUpdateStep(stepId, {
        type: 'updateAction',
        toUpdate: actionId,
        update: {
          type: 'makeMixture',
          finalComposition: { type: 'update', toUpdate: id, [field]: { [key]: value } },
        },
      });
    };

  const handleUpdateFinalVolume = (key: string, value: string) => {
    handleUpdateStep(stepId, {
      type: 'updateAction',
      toUpdate: actionId,
      update: {
        type: 'makeMixture',
        finalVolume: { [key]: value },
      },
    });
  };

  const handleUpdateMode = (id: string, mode: string) => {
    handleUpdateStep(stepId, {
      type: 'updateAction',
      toUpdate: actionId,
      update: {
        type: 'makeMixture',
        additions: { type: 'update', toUpdate: id, mode },
      },
    });
  };

  const handleUpdateValue = (id: string) => (key: string, value: string) => {
    handleUpdateStep(stepId, {
      type: 'updateAction',
      toUpdate: actionId,
      update: {
        type: 'makeMixture',
        additions: { type: 'update', toUpdate: id, volume: { [key]: value } },
      },
    });
  };

  const sections: PageSection[] = [
    {
      label: 'Input Liquids',
      value: action.source?.name!,
    },
    {
      label: 'Target Output',
      value: step.target?.name!,
    },
    {
      label: 'Output Plate',
      value: step.target?.plate?.description!,
    },
  ];

  return (
    <FlexContainer component={Paper}>
      <FlexContainer direction="column">
        <PageNavigation
          backTo={discoPrototypeRoutes.root.getPath()}
          title="Edit Mixtures"
          sections={sections}
        />
        <ErrorsAlert severity="warning" errors={errors} />
        <FlexContainer direction="row">
          <FlexContainer
            component={Paper}
            direction="column"
            scroll="auto"
            sx={{ maxWidth: '300px', p: '16px' }}
          >
            <Stack spacing={4}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h2">Liquid addition order</Typography>
                <ChunkyButton
                  backgroundColor="white"
                  startIcon={<AddIcon />}
                  onClick={handleAddLiquid}
                >
                  Add
                </ChunkyButton>
              </Box>
              <Stack spacing={4}>
                {additions.map((addition, idx) => (
                  <Stack spacing={4} key={addition.id}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="subtitle1">Liquid {idx + 1}</Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteLiquid(addition.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <StepSelect
                      value={addition.sourceName}
                      label="Select liquid"
                      options={liquidOptions}
                      onChange={e => handleUpdateLiquid(addition.id, e.target.value)}
                    />
                    <StepSelect
                      value={addition.mode}
                      label="Mode"
                      options={modeOptions}
                      onChange={e => handleUpdateMode(addition.id, e.target.value)}
                    />
                    {addition.mode === 'volume' && (
                      <ValueInput
                        volume={addition.volume}
                        options={columnOptions}
                        handleUpdateValue={handleUpdateValue(addition.id)}
                      />
                    )}
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </FlexContainer>
          <FlexContainer
            direction="column"
            p={4}
            scroll="auto"
            sx={{
              background: '#f6f6f6',
            }}
          >
            <Box sx={{ alignSelf: 'flex-start' }}>
              <SimpleTable
                table={step.target?.table.compiled || step.target?.table}
                headers={tableHeaders}
                columnNames={tableColumns}
              />
            </Box>
          </FlexContainer>
          <FlexContainer
            component={Paper}
            direction="column"
            scroll="auto"
            sx={{ maxWidth: '300px', p: '16px' }}
          >
            <Stack spacing={8}>
              <StepCheckbox
                label="Set concentrations for this action"
                checked={action.isConstrained ?? false}
                onChange={e => handleIsConstrained(e.target.checked)}
              />
              {action.isConstrained && (
                <>
                  <Stack spacing={4}>
                    <Typography variant="h2">Final Volume</Typography>
                    <ValueInput
                      volume={action.finalVolume}
                      options={columnOptions}
                      handleUpdateValue={handleUpdateFinalVolume}
                    />
                  </Stack>
                  <Stack spacing={4}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      gap={2}
                    >
                      <Typography variant="h2">Composition</Typography>
                      <ChunkyButton
                        backgroundColor="white"
                        startIcon={<AutoFixNormalIcon />}
                        onClick={handleAutoFinalComposition}
                      >
                        Auto
                      </ChunkyButton>
                      <ChunkyButton
                        backgroundColor="white"
                        startIcon={<AddIcon />}
                        onClick={handleAddFinalComposition}
                      >
                        Add
                      </ChunkyButton>
                    </Box>
                    {finalComposition.map((fc, idx) => (
                      <Stack spacing={4} key={fc.id}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography variant="subtitle1">Component {idx + 1}</Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteFinalComposition(fc.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Stack spacing={4}>
                          <Typography variant="subtitle2">Name</Typography>
                          <ValueInput
                            volume={fc.name}
                            options={columnOptions}
                            constOptions={componentsOptions}
                            handleUpdateValue={handleUpdateFinalComposition(
                              fc.id,
                              'name',
                            )}
                          />
                        </Stack>
                        {fc.name.value && (
                          <Stack spacing={4}>
                            <Typography variant="subtitle2">Concentration</Typography>
                            <ValueInput
                              volume={fc.concentration}
                              options={columnOptions}
                              handleUpdateValue={handleUpdateFinalComposition(
                                fc.id,
                                'concentration',
                              )}
                            />
                          </Stack>
                        )}
                      </Stack>
                    ))}
                  </Stack>
                </>
              )}
              <Stack spacing={4}>
                <ChunkyButton
                  backgroundColor="white"
                  startIcon={<AddIcon />}
                  onClick={handleCalculateVolumes}
                >
                  Calculate Volumes
                </ChunkyButton>
              </Stack>
            </Stack>
          </FlexContainer>
        </FlexContainer>
      </FlexContainer>
    </FlexContainer>
  );
};

export default Page;
